/**
 * Structured Logger Utility
 * Standardizes logging across the application and allows for easy filtering or external piping.
 *
 * Usage:
 *   import { createLogger } from '@/lib/logger'
 *   const log = createLogger('my-module')
 *   log.info('action', 'message', { key: 'value' })
 *   await log.trace('fetchData', () => fetch(...), { userId })
 *
 * Legacy usage (still works):
 *   import { logger } from '@/lib/logger'
 *   logger.info('message', { correlationId: '...' })
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
}

// Default to 'info' in production, 'debug' in development
// Handle Node.js execution where import.meta.env is undefined
const params = (import.meta as unknown as { env: Record<string, string | boolean> }).env || {}
const CURRENT_LOG_LEVEL: LogLevel = params.PROD ? 'info' : 'debug'

const shouldLog = (level: LogLevel): boolean => {
    return LOG_LEVELS[level] >= LOG_LEVELS[CURRENT_LOG_LEVEL]
}

export interface LogContext {
    correlationId?: string
    [key: string]: unknown
}

export const generateCorrelationId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `cid-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`
}

function isLogContext(arg: unknown): arg is LogContext {
    return typeof arg === 'object' && arg !== null && 'correlationId' in arg
}

const formatMessage = (level: LogLevel, message: string, ...args: unknown[]) => {
    let prefix = `[${level.toUpperCase()}]`

    // Check if the first argument provides context with a correlation ID
    if (args.length > 0 && isLogContext(args[0])) {
        const context = args[0]
        if (context.correlationId) {
            prefix += ` [CID:${context.correlationId}]`
        }
    }

    return [`${prefix} ${message}`, ...args]
}

// ---------------------------------------------------------------------------
// Legacy logger (backward-compatible — 102 existing usages)
// ---------------------------------------------------------------------------
export const logger = {
    debug: (message: string, ...args: unknown[]) => {
        if (shouldLog('debug')) {
            console.debug(...formatMessage('debug', message, ...args))
        }
    },
    info: (message: string, ...args: unknown[]) => {
        if (shouldLog('info')) {
            console.info(...formatMessage('info', message, ...args))
        }
    },
    warn: (message: string, ...args: unknown[]) => {
        if (shouldLog('warn')) {
            console.warn(...formatMessage('warn', message, ...args))
        }
    },
    error: (message: string, ...args: unknown[]) => {
        if (shouldLog('error')) {
            console.error(...formatMessage('error', message, ...args))
        }
    },
}

// ---------------------------------------------------------------------------
// Module-scoped logger factory (new — preferred for all new code)
// ---------------------------------------------------------------------------

export interface ModuleLogger {
    debug: (action: string, message: string, data?: Record<string, unknown>) => void
    info: (action: string, message: string, data?: Record<string, unknown>) => void
    warn: (action: string, message: string, data?: Record<string, unknown>) => void
    error: (action: string, message: string, data?: Record<string, unknown>) => void
    /** Wraps an async function with automatic duration tracking and error logging. */
    trace: <T>(action: string, fn: () => Promise<T>, context?: Record<string, unknown>) => Promise<T>
}

export function createLogger(module: string): ModuleLogger {
    const log = (level: LogLevel, action: string, message: string, data?: Record<string, unknown>) => {
        if (!shouldLog(level)) return

        const prefix = `[${module}:${action}]`
        const args: unknown[] = data ? [data] : []

        switch (level) {
            case 'error':
                console.error(prefix, message, ...args)
                break
            case 'warn':
                console.warn(prefix, message, ...args)
                break
            case 'debug':
                console.debug(prefix, message, ...args)
                break
            default:
                console.info(prefix, message, ...args)
        }
    }

    return {
        debug: (action, message, data?) => log('debug', action, message, data),
        info: (action, message, data?) => log('info', action, message, data),
        warn: (action, message, data?) => log('warn', action, message, data),
        error: (action, message, data?) => log('error', action, message, data),

        async trace<T>(action: string, fn: () => Promise<T>, context?: Record<string, unknown>): Promise<T> {
            const start = performance.now()
            log('debug', action, 'started', context)
            try {
                const result = await fn()
                const duration_ms = Math.round(performance.now() - start)
                log('info', action, `completed in ${duration_ms}ms`, { ...context, duration_ms })
                return result
            } catch (error) {
                const duration_ms = Math.round(performance.now() - start)
                log('error', action, `failed after ${duration_ms}ms`, {
                    ...context,
                    duration_ms,
                    error: error instanceof Error ? error.message : String(error),
                })
                throw error
            }
        },
    }
}
